import { HardDrive } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';

const PROVIDER_LABELS = {
  local_storage: 'Local Storage', google_drive: 'Google Drive', dropbox: 'Dropbox', box: 'Box', onedrive: 'OneDrive',
};

function formatBytes(n) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

export default function SuperAdminStorageHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/admin/storage-stats')
      .then(res => setData(res.data.data))
      .catch(() => setError('Failed to load storage stats.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-sm text-[#706E6B]">Loading…</div>;
  if (error) return <div className="p-8 text-sm text-[#BA0517]">{error}</div>;

  const usageByProvider = Object.fromEntries((data.usageByProvider || []).map(u => [u._id, u]));
  const connectedByProvider = Object.fromEntries((data.connectedByProvider || []).map(c => [c._id, c.count]));

  return (
    <div className="w-full pb-10 space-y-6">
      <PageHeader title="Storage Health" icon={HardDrive} subtitle="Platform-wide Cloud Storage connector usage, connections, and sync errors across every tenant." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <p className="text-xs text-[#706E6B]">Companies using storage</p>
          <p className="text-2xl font-bold text-[#080707]">{data.totalCompaniesWithStorage}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <p className="text-xs text-[#706E6B]">Accounts with a sync error</p>
          <p className={`text-2xl font-bold ${data.syncErrorCount > 0 ? 'text-[#BA0517]' : 'text-[#080707]'}`}>{data.syncErrorCount}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <p className="text-xs text-[#706E6B]">Total bytes stored</p>
          <p className="text-2xl font-bold text-[#080707]">
            {formatBytes((data.usageByProvider || []).reduce((sum, u) => sum + (u.totalBytes || 0), 0))}
          </p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <h3 className="text-sm font-semibold text-[#2E2E2E] mb-3">By Provider</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {['local_storage', 'google_drive', 'dropbox', 'box', 'onedrive'].map(provider => {
            const usage = usageByProvider[provider];
            return (
              <div key={provider} className="border border-[#F3F2F2] rounded-lg p-3">
                <p className="text-xs font-medium text-[#444444]">{PROVIDER_LABELS[provider]}</p>
                <p className="text-xs text-[#706E6B] mt-1">{connectedByProvider[provider] || 0} connected accounts</p>
                <p className="text-xs text-[#706E6B]">{usage?.fileCount || 0} files · {formatBytes(usage?.totalBytes)}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <h3 className="text-sm font-semibold text-[#2E2E2E] mb-3">Recent Sync Errors (24h)</h3>
        {(data.recentErrorLogs || []).length === 0 ? (
          <p className="text-sm text-[#706E6B]">No sync errors in the last 24 hours.</p>
        ) : (
          <div className="divide-y divide-[#F3F2F2] max-h-96 overflow-y-auto">
            {data.recentErrorLogs.map(log => (
              <div key={log._id} className="py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#444444]">{PROVIDER_LABELS[log.provider] || log.provider}</span>
                  <span className="text-xs text-[#706E6B]">{new Date(log.createdAt).toLocaleString()}</span>
                </div>
                <p className="text-xs text-[#BA0517] mt-0.5">{log.metadata?.error || 'Unknown error'}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
